import { collection, doc, getDoc, getDocs, setDoc, query, orderBy, where, increment, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { validateWorkout, normalizeLegacy, unitForScoreType } from '../schemas/workout';

/**
 * GlobalWorkout Model
 *
 * Manages read-only global workout library
 * Firestore path: globalWorkouts/{workoutId}
 */
class GlobalWorkout {
  static _normalize(data) {
    const d = normalizeLegacy({
      ...data,
      name: data.name ?? '',
      description: data.description ?? '',
      exercises: Array.isArray(data.exercises) ? data.exercises : [],
      popularity: data.popularity ?? 0,
    });
    // Derived field for backward compat with pages that read measurementUnit
    d.measurementUnit = unitForScoreType(d.scoreType);
    return d;
  }

  /**
   * Fetch all global workouts ordered by popularity
   */
  static async getAll({ requirePopularity = true } = {}) {
    try {
      const q = requirePopularity
        ? query(collection(db, 'globalWorkouts'), orderBy('popularity', 'desc'))
        : query(collection(db, 'globalWorkouts'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => GlobalWorkout._normalize({
        id: doc.id,
        ...doc.data(),
        isGlobal: true
      }));
    } catch (error) {
      console.error('Error fetching global workouts:', error);
      throw error;
    }
  }

  /**
   * Fetch single global workout by ID
   */
  static async getById(workoutId) {
    try {
      const docRef = doc(db, 'globalWorkouts', workoutId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return GlobalWorkout._normalize({
          id: docSnap.id,
          ...docSnap.data(),
          isGlobal: true
        });
      }
      return null;
    } catch (error) {
      console.error('Error fetching global workout:', error);
      throw error;
    }
  }

  /**
   * Filter global workouts by category
   */
  static async getByCategory(category) {
    try {
      const q = query(
        collection(db, 'globalWorkouts'),
        where('category', '==', category),
        orderBy('popularity', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => GlobalWorkout._normalize({
        id: doc.id,
        ...doc.data(),
        isGlobal: true
      }));
    } catch (error) {
      console.error('Error fetching global workouts by category:', error);
      throw error;
    }
  }

  /**
   * Increment popularity count
   */
  static async incrementPopularity(workoutId) {
    try {
      const docRef = doc(db, 'globalWorkouts', workoutId);
      await updateDoc(docRef, {
        popularity: increment(1)
      });
    } catch (error) {
      console.error('Error incrementing popularity:', error);
      throw error;
    }
  }

  /**
   * Create a new global workout
   */
  static async create(data) {
    const validation = validateWorkout(data);
    if (!validation.success) {
      throw new Error(`Validation failed: ${validation.error}`);
    }

    try {
      const id = data.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      const docRef = doc(db, 'globalWorkouts', id);
      await setDoc(docRef, {
        ...validation.data,
        popularity: validation.data.popularity || 0,
        createdAt: new Date().toISOString(),
      });
      return id;
    } catch (error) {
      console.error('Error creating global workout:', error);
      throw error;
    }
  }

  /**
   * Update an existing global workout
   */
  static async update(workoutId, data) {
    const validation = validateWorkout(data);
    if (!validation.success) {
      throw new Error(`Validation failed: ${validation.error}`);
    }

    try {
      const docRef = doc(db, 'globalWorkouts', workoutId);
      await updateDoc(docRef, {
        ...validation.data,
        updatedAt: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error updating global workout:', error);
      throw error;
    }
  }

  /**
   * Delete a global workout
   */
  static async delete(workoutId) {
    try {
      const docRef = doc(db, 'globalWorkouts', workoutId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting global workout:', error);
      throw error;
    }
  }

  /**
   * Decrement popularity count
   */
  static async decrementPopularity(workoutId) {
    try {
      const docRef = doc(db, 'globalWorkouts', workoutId);
      await updateDoc(docRef, {
        popularity: increment(-1)
      });
    } catch (error) {
      console.error('Error decrementing popularity:', error);
      throw error;
    }
  }
}

export default GlobalWorkout;
